import gsap from 'gsap';
import { Portal } from 'solid-js/web';

import type {
  OpenSliderCaptchaOptions,
  SliderCaptchaChallenge,
  SliderCaptchaProps,
  SliderCaptchaResult,
  SliderCaptchaResultReason,
} from './type';
import {
  adaptImageCaptchaToChallenge,
  clamp,
  getPuzzleOffset,
  getSliderProgress,
  HANDLE_GAP,
  HANDLE_SIZE,
} from './utils';

interface SliderCaptchaDialogProps extends SliderCaptchaProps {
  dialogClass?: string;
  closeOnOverlay?: boolean;
  onClose: () => void;
}

type SliderCaptchaDialogItem = {
  id: number;
  options: OpenSliderCaptchaOptions;
  resolve: (result: SliderCaptchaResult) => void;
};

type SliderCaptchaManager = {
  open: (options?: OpenSliderCaptchaOptions) => Promise<SliderCaptchaResult>;
};

declare global {
  interface Window {
    __sliderCaptchaManager__?: SliderCaptchaManager;
  }
}

function getSliderCaptchaManager() {
  if (typeof window === 'undefined') return undefined;
  return window.__sliderCaptchaManager__;
}

function SliderCaptchaDialog(props: SliderCaptchaDialogProps) {
  return (
    <Portal>
      <div
        class="fixed inset-0 z-[1200] flex items-center justify-center bg-[rgba(16,18,18,0.8)] p-4"
        onClick={(event) => {
          if (event.target !== event.currentTarget || !props.closeOnOverlay) return;
          props.onClose();
        }}
      >
        <div
          class={cx('relative flex max-h-[80%] w-[420px] flex-col bg-transparent shadow-none', props.dialogClass)}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            class="absolute right-4 top-1 z-10 flex size-8 items-center justify-center rounded-full text-neutral-main2 transition-colors hover:text-brand-default"
            onClick={props.onClose}
          >
            <IconCloseMark class="size-4" />
          </button>
          <SliderCaptcha
            class={props.class}
            title={props.title}
            helperText={props.helperText}
            onFail={props.onFail}
            onSuccess={props.onSuccess}
          />
        </div>
      </div>
    </Portal>
  );
}

async function runDialogCallback(
  reason: SliderCaptchaResultReason,
  options: OpenSliderCaptchaOptions,
  result?: { captchaKey: string; xcoordinate: number }
) {
  try {
    if (reason === 'success') {
      await options.onSuccess?.(result!);
      return;
    }

    await options.onCancel?.();
  } catch (error: any) {
    console.error('[SliderCaptcha] Dialog callback failed:', error);
  }
}

export function openSliderCaptcha(options: OpenSliderCaptchaOptions = {}): Promise<SliderCaptchaResult> {
  const manager = getSliderCaptchaManager();
  if (!manager) {
    console.warn('[SliderCaptcha] Provider is not mounted yet.');
    return Promise.resolve({
      verified: false,
      reason: 'cancel',
    });
  }
  return manager.open(options);
}

export function SliderCaptchaProvider() {
  const [sliderCaptchaDialogs, setSliderCaptchaDialogs] = createSignal<SliderCaptchaDialogItem[]>([]);
  let nextSliderCaptchaId = 0;

  const finishSliderCaptchaDialog = async (
    id: number,
    reason: SliderCaptchaResultReason,
    result?: { captchaKey: string; xcoordinate: number }
  ) => {
    const dialog = sliderCaptchaDialogs().find((item) => item.id === id);
    if (!dialog) return;

    setSliderCaptchaDialogs((items) => items.filter((item) => item.id !== id));
    await runDialogCallback(reason, dialog.options, result);
    dialog.resolve({
      verified: reason === 'success',
      reason,
    });
  };

  onMount(() => {
    const manager: SliderCaptchaManager = {
      open: (options: OpenSliderCaptchaOptions = {}) =>
        new Promise((resolve) => {
          const id = ++nextSliderCaptchaId;
          setSliderCaptchaDialogs((items) => [...items, { id, options, resolve }]);
        }),
    };

    window.__sliderCaptchaManager__ = manager;

    onCleanup(() => {
      if (window.__sliderCaptchaManager__ === manager) {
        delete window.__sliderCaptchaManager__;
      }
    });
  });

  return (
    <For each={sliderCaptchaDialogs()}>
      {(dialog) => (
        <SliderCaptchaDialog
          class={dialog.options.class}
          dialogClass={dialog.options.dialogClass}
          closeOnOverlay={dialog.options.closeOnOverlay ?? true}
          onClose={() => {
            void finishSliderCaptchaDialog(dialog.id, 'cancel');
          }}
          title={dialog.options.title}
          helperText={dialog.options.helperText}
          onFail={dialog.options.onFail}
          onSuccess={({ captchaKey, xcoordinate }: { captchaKey: string; xcoordinate: number }) =>
            finishSliderCaptchaDialog(dialog.id, 'success', {
              captchaKey,
              xcoordinate,
            })
          }
        />
      )}
    </For>
  );
}

export function SliderCaptcha(props: SliderCaptchaProps) {
  let sliderTrackRef: HTMLDivElement | undefined;
  let pieceRef: HTMLImageElement | undefined;
  let captchaContainerRef: HTMLDivElement | undefined;
  let refreshTimer: number | undefined;
  let rafId = 0;
  let pendingOffset = 0;
  let dragStartX = 0;
  let dragStartOffset = 0;

  const createVerificationCaptchaVerify = useAction(post.createVerificationCaptchaVerify);

  const [challenge, setChallenge] = createSignal<SliderCaptchaChallenge | null>(null);
  const [isLoading, setIsLoading] = createSignal(true);
  const [isVerifying, setIsVerifying] = createSignal(false);
  const [isVerified, setIsVerified] = createSignal(false);
  const [isDragging, setIsDragging] = createSignal(false);
  const [sliderOffset, setSliderOffset] = createSignal(0);
  const [sliderMaxOffset, setSliderMaxOffset] = createSignal(0);
  const [verifyStatus, setVerifyStatus] = createSignal<'idle' | 'success' | 'fail'>('idle');
  const [boardWidth, setBoardWidth] = createSignal(0);

  const currentPieceOffset = createMemo(() => {
    const currentChallenge = challenge();
    return currentChallenge ? getPuzzleOffset(sliderOffset(), sliderMaxOffset(), currentChallenge.maxOffset) : 0;
  });
  const sliderProgress = createMemo(() => getSliderProgress(sliderOffset(), sliderTrackRef?.clientWidth || 0));
  const isHandleDisabled = createMemo(() => isLoading() || isVerifying() || isVerified());
  const title = () => props.title ?? t('Security verification');
  const helperText = () => props.helperText ?? t('Drag the slider below to complete the verification.');

  const animatePieceShake = () => {
    if (!pieceRef) return;
    const baseX = currentPieceOffset();

    gsap.killTweensOf(pieceRef);
    gsap.to(pieceRef, {
      x: baseX - 6,
      duration: 0.084,
      ease: 'power1.inOut',
      repeat: 4,
      yoyo: true,
      onComplete: () => {
        gsap.to(pieceRef, { x: baseX, duration: 0.1, ease: 'power2.out' });
      },
    });
  };

  const measureSliderMaxOffset = () => {
    if (!sliderTrackRef) return;
    setSliderMaxOffset(Math.max(sliderTrackRef.clientWidth - HANDLE_SIZE - HANDLE_GAP * 2, 0));
  };

  const scheduleVisualUpdate = (offset: number) => {
    pendingOffset = offset;
    if (rafId) return;

    rafId = window.requestAnimationFrame(() => {
      rafId = 0;
      setSliderOffset(pendingOffset);
    });
  };

  const clearTimersAndAnimations = () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = undefined;
    }
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }

    if (pieceRef) {
      gsap.killTweensOf(pieceRef);
      gsap.set(pieceRef, { x: 0, clearProps: 'transform' });
    }
  };

  const resetSlider = () => {
    clearTimersAndAnimations();
    pendingOffset = 0;
    setSliderOffset(0);
    setIsDragging(false);
    setIsVerified(false);
    measureSliderMaxOffset();
  };

  const loadCaptcha = async () => {
    setIsLoading(true);
    setIsVerifying(false);
    setVerifyStatus('idle');
    setChallenge(null);

    try {
      const response = await getOnce.getUsersVerificationCaptcha();
      const currentBoardWidth = boardWidth();
      if (currentBoardWidth > 0) {
        setChallenge(adaptImageCaptchaToChallenge(response, currentBoardWidth));
      }
      resetSlider();
    } catch (error: any) {
      console.error('Failed to load captcha:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOffset = async () => {
    const currentChallenge = challenge();
    if (!currentChallenge || isVerified()) return;

    const currentBoardWidth = boardWidth();
    const rateLimit = currentBoardWidth > 0 ? currentBoardWidth / 300 : 1;
    const puzzleOffset = Math.round(currentPieceOffset() / rateLimit);

    setIsVerifying(true);
    setVerifyStatus('idle');

    try {
      const result = await createVerificationCaptchaVerify({
        captchaKey: currentChallenge.challengeId,
        xcoordinate: puzzleOffset,
      });

      if (result.success) {
        setIsVerified(true);
        setVerifyStatus('success');

        setTimeout(async () => {
          await props.onSuccess?.({
            captchaKey: currentChallenge.challengeId,
            xcoordinate: puzzleOffset,
          });
        }, 500);

        return;
      }

      animatePieceShake();
      setVerifyStatus('fail');
      await props.onFail?.({
        captchaKey: currentChallenge.challengeId,
        xcoordinate: puzzleOffset,
      });

      refreshTimer = window.setTimeout(() => {
        void loadCaptcha();
      }, 500);
    } catch (error: any) {
      console.error('Verification failed:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const startDragging = (clientX: number) => {
    if (isHandleDisabled()) return;
    setIsDragging(true);
    dragStartX = clientX;
    dragStartOffset = sliderOffset();
  };

  const moveDragging = (clientX: number) => {
    if (!isDragging()) return;
    const nextOffset = clamp(dragStartOffset + clientX - dragStartX, 0, sliderMaxOffset());
    scheduleVisualUpdate(nextOffset);
  };

  const endDragging = () => {
    if (!isDragging()) return;
    setIsDragging(false);
    setSliderOffset(pendingOffset);
    verifyOffset();
  };

  onMount(() => {
    if (captchaContainerRef) {
      const measureWidth = () => {
        const width = captchaContainerRef?.clientWidth || 0;
        if (width > 0) {
          setBoardWidth(width);
        }
      };

      measureWidth();

      const resizeObserver = new ResizeObserver(() => {
        measureWidth();
      });

      resizeObserver.observe(captchaContainerRef);

      onCleanup(() => {
        resizeObserver.disconnect();
      });
    }

    measureSliderMaxOffset();
    loadCaptcha();

    const handleResize = () => {
      measureSliderMaxOffset();
      setSliderOffset((current) => clamp(current, 0, sliderMaxOffset()));
      pendingOffset = sliderOffset();
    };

    window.addEventListener('resize', handleResize);

    onCleanup(() => {
      window.removeEventListener('resize', handleResize);
      clearTimersAndAnimations();
    });
  });

  return (
    <div class={cx('w-full', props.class)}>
      <div class=" p-6 bg-layer3_5-card rounded-lg">
        <div class="mb-3 flex items-center justify-between gap-3">
          <div>
            <div class="text-neutral-main1 text-xl font-bold">{title()}</div>
            <div class="text-neutral-main2 text-sm font-medium">{helperText()}</div>
          </div>
        </div>

        <div
          ref={captchaContainerRef}
          class="relative max-w-full"
          style={{ 'aspect-ratio': '2 / 1' }}
          data-dragging={isDragging() ? 'true' : 'false'}
        >
          <Show
            when={challenge()}
            fallback={
              <>
                <div class=" w-full h-[186px] center bg-layer4 border border-main3 rounded-lg">
                  <BarLoading></BarLoading>
                </div>
              </>
            }
          >
            {(currentChallenge) => (
              <>
                <img
                  src={currentChallenge().backgroundUrl}
                  alt={t('Captcha background')}
                  class="block h-full w-full select-none object-cover rounded-lg"
                  draggable={false}
                />

                <img
                  ref={pieceRef}
                  src={currentChallenge().pieceUrl}
                  alt={t('Captcha puzzle piece')}
                  class="absolute left-0 top-0 h-auto select-none will-change-transform"
                  draggable={false}
                  style={{
                    top: `${currentChallenge().pieceY}px`,
                    width: `${currentChallenge().pieceSize}px`,
                    height: `${currentChallenge().pieceSize}px`,
                    transform: `translate3d(${currentPieceOffset()}px, 0, 0)`,
                  }}
                />
                <Show when={verifyStatus() === 'success' || verifyStatus() === 'fail'}>
                  <div
                    class={cx(
                      `absolute bottom-0 left-0 h-6 w-full rounded-lg pointer-events-none  rounded-tl-none rounded-tr-none center p-4 font-semibold`,
                      verifyStatus() === 'success' && 'bg-brand text-neutral-alw-black ',
                      verifyStatus() === 'fail' && 'text-neutral-main1 bg-red'
                    )}
                  >
                    <span>
                      {verifyStatus() === 'success' && t('Complete, well done!')}
                      {verifyStatus() === 'fail' && t('Failed, please try again!')}
                    </span>
                  </div>
                </Show>
              </>
            )}
          </Show>
        </div>

        <div class="mt-7">
          <div ref={sliderTrackRef} class="relative h-[14px] overflow-hidden rounded-full bg-alpha-neutral-white-a10">
            <div
              class={cx(
                'absolute inset-0 origin-left  will-change-transform  rounded-full',
                !isDragging() && 'transition-transform duration-100 ease-out',
                verifyStatus() === 'success' && 'bg-brand',
                verifyStatus() === 'fail' && 'bg-red',
                verifyStatus() === 'idle' && 'bg-[var(--text-neutral-main7)]'
              )}
              style={{
                transform: `scaleX(${sliderProgress()})`,
              }}
            />
          </div>
          <div class=" w-full relative mt-2">
            <button
              aria-label={t('Drag the slider')}
              class={cx(
                'absolute left-0 -top-[40px] z-10 flex h-[44px] w-[58px] items-center justify-center will-change-transform bg-layer9 rounded-lg',
                isDragging() ? 'cursor-grabbing' : 'cursor-grab',
                isHandleDisabled() && 'cursor-not-allowed opacity-60',
                verifyStatus() === 'success' && '!bg-brand !opacity-100',
                verifyStatus() === 'fail' && ' !bg-red !opacity-100  '
              )}
              style={{ transform: `translate3d(${sliderOffset()}px, 0, 0)` }}
              disabled={isHandleDisabled()}
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
                startDragging(event.clientX);
              }}
              onPointerMove={(event) => moveDragging(event.clientX)}
              onPointerUp={endDragging}
              onPointerCancel={endDragging}
            >
              {!isVerifying() && verifyStatus() === 'idle' && (
                <IconArrow08 class="-rotate-90 text-neutral-main1 size-4"></IconArrow08>
              )}
              {isVerifying() && <IconLoading class="animate-spin text-neutral-main1 size-4" />}
              {!isVerifying() && verifyStatus() === 'success' && (
                <IconCheckMark class=" size-4 text-neutral-alw-black font-semibold" />
              )}
              {!isVerifying() && verifyStatus() === 'fail' && (
                <IconClose class="text-neutral-main1 size-3 font-semibold" />
              )}
            </button>
          </div>
        </div>

        <div class=" flex justify-end mt-4">
          <button
            type="button"
            class="text-[13px] font-medium leading-[1.5] text-brand-default transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
            disabled={isLoading() || isVerifying()}
            onClick={() => void loadCaptcha()}
          >
            {t('Refresh')}
          </button>
        </div>
      </div>
    </div>
  );
}
